import { useContext } from 'react'
import { Play } from 'lucide-react'
import { useStrictLang } from './design/strictLangContext'
import { blockTheme, hsl } from '@teach/ui'
import type { VideoBlock } from '@/types/blocks'
import type { ContentLang } from '@/features/editor/editorSlice'
import type { PreviewRun } from '../../preview/previewRun'
import { getLocalizedDisplay } from '@/features/editor/lib/localizedField'
import { PreviewShell } from './PreviewShell'
import { PreviewViewportContext, useIsImmersiveViewport } from './previewViewports'
import { useVideoOrientation } from '@/features/editor/components/shared/useVideoOrientation'
import { VideoPlayerPreview } from '@/features/editor/components/shared/VideoPlayerPreview'
import { VideoPosterFrame } from '@/features/editor/components/shared/VideoPosterFrame'
import { CanvasBridgeDrop, CanvasBridgeProgress, useCanvasVideoDrop } from './canvasVideoDrop'
import { videoUploadBridge } from '@/features/editor/lib/videoUploadBridge'
import { pickMessages, useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  block: VideoBlock
  contentLang: ContentLang
  /** Прохождение: нижняя кнопка ведёт на следующий экран. */
  run?: PreviewRun
}

export const VideoBlockPreview = ({ block, contentLang, run }: Props) => {
  const m = useMessages(messages)
  // Экран ученика: кнопки и заголовки — на языке курса, как в плеере.
  const p = pickMessages(messages, contentLang)
  // Холст и миниатюры редактора — строго язык правки; прохождение — как плеер.
  const strictLang = useStrictLang() && (!run || run.onCanvas === true)
  // Показ, не правка: симметричный фолбэк — у английского курса на слоте RU
  // videoId пуст, видео лежит в videoIdEn (и наоборот для видео на RU-слоте).
  const canvasDrop = useCanvasVideoDrop()
  const videoId = getLocalizedDisplay(block, 'videoId', contentLang, strictLang)
  const hasVideo = Boolean(videoId)
  // На холсте и в прохождении ролик можно запустить; в миниатюрах — только значок.
  const playable = canvasDrop || Boolean(run)
  const immersive = useIsImmersiveViewport()
  const viewport = useContext(PreviewViewportContext)
  const isLandscape = useVideoOrientation(playable ? videoId : null) === 'landscape'
  const fitWidth = isLandscape && viewport === 'phone'

  return (
    <PreviewShell
      cta={p.next}
      onCta={run?.onCta}
      darkChrome={hasVideo ? playable : false}
      showBack={false}
      style={{
        backgroundColor:
          hasVideo && playable ? hsl(blockTheme.foreground) : hsl(blockTheme.background),
      }}
      contentClassName={
        hasVideo && playable
          ? immersive
            ? 'p-0'
            : 'items-center px-0 py-2'
          : hasVideo
            ? undefined
            : 'items-center justify-center p-6'
      }
      // Ref MobilePreviewFrame.tsx:661-664 (`case 'video'`): заполненное видео —
      // такой же full-bleed слой, как картинка у info, и уходит ПОД прогресс-бар
      // и CTA. Так же его показывают оба плеера (веб — своя широкая модалка на
      // весь лист, мобилка — на всю площадь блока), поэтому зажатый в контентную
      // область чёрный прямоугольник врал про кадр.
      fullBleed={
        hasVideo && !playable && videoId ? <VideoPosterFrame videoId={videoId} /> : undefined
      }
    >
      {/* Прохождение: ученик без ролика видит пустой экран с фоном урока, а не
          авторскую подсказку «Видео не загружено» — экран ученика лгать не должен. */}
      {/* Плеер — МЕЖДУ полоской прогресса и кнопкой, на всю ширину экрана: под
          ними его управление было бы недоступно. */}
      {hasVideo && playable ? (
        // Горизонтальный ролик на экране телефона — плеер ровно 16:9 по ширине
        // (поля сверху и снизу): иначе плеер растягивает кадр и режет края.
        <div
          className={
            fitWidth
              ? 'relative aspect-video w-full overflow-hidden bg-black'
              : 'relative h-full w-full overflow-hidden bg-black'
          }
        >
          <VideoPlayerPreview videoId={videoId} />
          {canvasDrop ? <CanvasBridgeProgress bridge={videoUploadBridge} /> : null}
        </div>
      ) : hasVideo || run ? null : canvasDrop ? (
        <CanvasBridgeDrop
          bridge={videoUploadBridge}
          accept={{ 'video/*': [] }}
          label={m.dropVideo}
          subtitle={m.videoFormats}
        />
      ) : (
        // Миниатюра пустого экрана — просто крупная иконка, без подложки и текста.
        <Play
          aria-label={m.a11yVideo}
          role="img"
          className="size-20"
          strokeWidth={1.5}
          style={{ color: hsl(blockTheme.foreground, 0.3) }}
        />
      )}
    </PreviewShell>
  )
}
